// Downscale + re-encode a photo in the browser before it is uploaded.
//
// A photo straight off a phone camera is routinely 8–12 MB at 4000px+, which the
// listing-images bucket (5 MB) rejects outright, and which costs the agent mobile
// data and the company storage for detail no listing page ever shows. 1920px on the
// long edge is more than any gallery renders, and lands a typical camera photo
// under 1 MB.
//
// Deliberately conservative: anything it cannot decode (HEIC on non-Safari, a
// corrupt file) or cannot actually make smaller is returned untouched, so a failure
// here degrades to the old behaviour rather than blocking the upload.
//
// KEEP IN SYNC with cid_admin_portal/src/utils/compressImage.js.

const MAX_EDGE = 1920;
const TARGET_BYTES = 1.5 * 1024 * 1024;
// Stepped rather than a single quality: most photos clear the target at 0.82 and
// keep their detail; only the stubborn ones pay for the lower settings.
const QUALITY_STEPS = [0.82, 0.7, 0.6, 0.5];

const toBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const asJpgName = (name) => `${String(name || 'photo').replace(/\.[^.]+$/, '')}.jpg`;

/**
 * @param {File} file
 * @returns {Promise<File>} the compressed file, or the original when compression
 *   is impossible or would not help.
 */
export async function compressImage(file, { maxEdge = MAX_EDGE, targetBytes = TARGET_BYTES } = {}) {
  if (!file || !file.type?.startsWith('image/')) return file;

  let bitmap;
  try {
    // `from-image` applies the EXIF orientation, so portrait phone photos don't
    // come out on their side — the tag is lost once we re-encode.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file; // undecodable (e.g. HEIC) — let the size guard report it instead
  }

  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    // Already small in both senses — re-encoding would only lose quality.
    if (scale === 1 && file.size <= targetBytes) return file;

    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    // JPEG has no alpha: without this, a transparent PNG renders on black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);

    let best = null;
    for (const quality of QUALITY_STEPS) {
      const blob = await toBlob(canvas, 'image/jpeg', quality);
      if (!blob) break;
      best = blob;
      if (blob.size <= targetBytes) break;
    }

    // Small or already-optimised files can come out bigger as JPEG — keep the original.
    if (!best || best.size >= file.size) return file;

    return new File([best], asJpgName(file.name), { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  } finally {
    bitmap.close?.();
  }
}

/**
 * Compress a picked list, in order. Failures fall through as the original file.
 *
 * Sequential rather than Promise.all: decoding several camera photos at once
 * spikes memory on a phone, and it lets `onProgress(done, total)` report honestly
 * — a batch of large photos takes long enough that silence reads as a hang.
 */
export async function compressAll(files, { onProgress, ...opts } = {}) {
  const list = Array.from(files);
  const out = [];
  for (const file of list) {
    out.push(await compressImage(file, opts));
    onProgress?.(out.length, list.length);
  }
  return out;
}
